import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function VerifyEmail() {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = searchParams.get('token');

  // Email used for resend: prefer location state (passed from Login on 403),
  // then stored user context, then nothing (user can type it in if needed)
  const emailForResend = location.state?.email || user?.email || '';

  const [status, setStatus] = useState('idle'); // idle | verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [sent, setSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setStatus('verifying');
    // Use raw axios — no auth header — so an expired JWT doesn't cause a 401
    // that triggers handleLogout() and kills the verification attempt
    axios.get(`${BASE_URL}/api/auth/verify-email/?token=${encodeURIComponent(token)}`)
      .then((res) => {
        setStatus('success');
        // Sync the verified state into context/localStorage so the login page
        // doesn't show a stale email_verified=false banner
        if (res.data?.email) {
          updateUser({ email_verified: true });
        }
        setTimeout(() => navigate('/login'), 2000);
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || 'Verification failed. The link may have expired.';
        setErrorMsg(msg);
        setStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleResend() {
    setResendLoading(true);
    try {
      await client.post('/api/auth/resend-verification/', { email: emailForResend });
      setSent(true);
    } finally {
      setResendLoading(false);
    }
  }

  if (token) {
    if (status === 'verifying') {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Verifying…</h1>
            <p style={styles.text}>Please wait while we verify your email.</p>
          </div>
        </div>
      );
    }
    if (status === 'success') {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Email verified!</h1>
            <p style={styles.text}>Your account is now active. Redirecting you to login…</p>
          </div>
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Verification failed</h1>
            <p style={styles.error}>{errorMsg}</p>
            <p style={styles.text}>Request a new link below.</p>
            {sent
              ? <p style={styles.success}>Verification email resent. Check your inbox.</p>
              : (
                <button style={styles.button} onClick={handleResend} disabled={resendLoading || !emailForResend}>
                  {resendLoading ? 'Sending…' : 'Resend verification email'}
                </button>
              )
            }
          </div>
        </div>
      );
    }
  }

  // ── No token: "check your inbox" page ────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Check your inbox</h1>
        <p style={styles.text}>
          We sent a verification link to <strong>{emailForResend || 'your email address'}</strong>.
          Click the link to activate your account.
        </p>
        {sent && <p style={styles.success}>Verification email resent. Check your inbox.</p>}
        {!sent && (
          <button style={styles.button} onClick={handleResend} disabled={resendLoading || !emailForResend}>
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f0f2f5',
  },
  card: {
    background: '#fff', padding: '2rem', borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxWidth: '400px', width: '100%',
    textAlign: 'center',
  },
  title: { margin: '0 0 1rem', fontSize: '1.5rem', color: '#2c3e50' },
  text: { color: '#555', lineHeight: 1.6, marginBottom: '1.5rem' },
  error: { color: '#721c24', background: '#f8d7da', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem' },
  success: { color: '#155724', background: '#d4edda', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem' },
  button: {
    padding: '0.7rem 1.4rem', background: '#2c3e50', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.95rem', cursor: 'pointer',
  },
};
